/* eslint-disable no-param-reassign, default-case */
import { createSelector } from 'reselect';
import produce from 'immer';
import get from 'lodash.get';
import { create } from 'services/api';
import isShowValid from 'validation/show';
import { ADD_SHOW_DRAFT, RECEIVE_SHOW_DRAFTS, RECEIVE_SHOW_EPISODES } from './shared-constants';
import { addManyToImmerState, addOneToImmerState, immerMergeArray, immerSet } from './util';

const RECEIVE_SHOWS = 'create/shows/RECEIVE';
const RECEIVE_SHOW = 'create/show/RECEIVE';
const UPDATE_SHOW = 'create/show/UPDATE';
const DELETE_SHOW = 'create/show/DELETE';
const CREATE_SHOW = 'create/show/CREATE';

export const RSS_SUBTITLE_LIMIT = 255;
export const DEFAULT_COVER_IMAGE = '/assets/images/default-episode.png';
export const getShowDescription = show => get(show, 'description', '');
export const selectShows = state => get(state, ['v2', 'shows'], null);
export const selectShowsTotal = state => get(state, ['v2', 'shows', 'total'], null);
export const selectShowsItems = createSelector(
  selectShows,
  shows => (shows ? Object.values(shows.items) : [])
);
export const selectShow = showId =>
  createSelector(
    selectShows,
    shows => get(shows, ['items', showId])
  );
export const selectEpisodes = state => get(state, ['v2', 'episodes'], {});
export const selectDrafts = state => get(state, ['v2', 'drafts'], {});
export const selectShowEpisodesTotal = showId =>
  createSelector(
    selectShows,
    shows => get(shows, ['items', showId, 'episodes', 'total'], null)
  );
export const selectShowDraftsTotal = showId =>
  createSelector(
    selectShows,
    shows => get(shows, ['items', showId, 'drafts', 'total'], null)
  );
export const selectShowEpisodes = showId =>
  createSelector(
    selectShows,
    selectEpisodes,
    (shows, episodes) =>
      get(shows, ['items', showId, 'episodes', 'items'], [])
        .map(epId => episodes.items[epId])
        .filter(e => e)
  );
export const selectShowDrafts = showId =>
  createSelector(
    selectShows,
    selectDrafts,
    (shows, drafts) =>
      get(shows, ['items', showId, 'drafts', 'items'], [])
        .map(draftsId => drafts.items[draftsId])
        .filter(e => e)
  );

const initialState = {
  total: null,
  items: {},
};
export default produce((state, action) => {
  switch (action.type) {
    case RECEIVE_SHOWS:
      addManyToImmerState(state, action.payload);
      break;
    case RECEIVE_SHOW:
    case UPDATE_SHOW:
      addOneToImmerState(state, action.payload);
      break;
    case DELETE_SHOW:
      state.total -= 1;
      delete state.items[action.payload];
      break;
    case RECEIVE_SHOW_EPISODES: {
      const receivedEpisodeIds = (action.payload.items || []).map(ep => ep.id);
      // Make sure show episodes object exists in state before setting total
      immerSet(state, ['items', action.payload.id, 'episodes', 'total'], action.payload.total);
      immerMergeArray(state, ['items', action.payload.id, 'episodes', 'items'], receivedEpisodeIds);
      break;
    }
    case RECEIVE_SHOW_DRAFTS: {
      const receivedDraftIds = (action.payload.items || []).map(draft => draft.id);
      // Make sure show drafts object exists in state before setting total
      immerSet(state, ['items', action.payload.id, 'drafts', 'total'], action.payload.total);
      immerMergeArray(state, ['items', action.payload.id, 'drafts', 'items'], receivedDraftIds);
      break;
    }
    case ADD_SHOW_DRAFT: {
      const { showId, draftId } = action.payload;
      immerMergeArray(state, ['items', showId, 'drafts', 'items'], [draftId]);
      break;
    }
    case CREATE_SHOW:
      addOneToImmerState(state, action.payload);
      break;
  }
}, initialState);

export const createShow = show => dispatch => {
  return create.post('/api-v2/channels', show).then(response => {
    dispatch({ type: CREATE_SHOW, payload: response.body });
    return Promise.resolve(response.body);
  });
};

export const fetchShows = () => dispatch => {
  return create.get('/api-v3/shows').then(response => {
    dispatch({ type: RECEIVE_SHOWS, payload: response.body });
    return Promise.resolve();
  });
};

export const fetchShowEpisodes = (showId, query) => dispatch => {
  return create.get(`/api-v3/shows/${showId}/episodes`, { query }).then(response => {
    dispatch({ type: RECEIVE_SHOW_EPISODES, payload: response.body });
    return Promise.resolve();
  });
};

export const fetchShowDrafts = (showId, query) => dispatch => {
  return create.get(`/api-v3/shows/${showId}/drafts`, { query }).then(response => {
    dispatch({ type: RECEIVE_SHOW_DRAFTS, payload: response.body });
    return Promise.resolve();
  });
};

function getShowFromApi(showId) {
  return create.get(`/api-v2/channels/${showId}`);
}
export const fetchShow = showId => (dispatch, getState) => {
  const state = getState();
  // The show in the state can have partial info with just the show episodes from fetchShowEpisodes
  // So validate that it's an object with full show info
  if (isShowValid(selectShow(showId)(state))) {
    return Promise.resolve();
  }

  return getShowFromApi(showId).then(response => {
    dispatch({ type: RECEIVE_SHOW, payload: response.body });
    return Promise.resolve();
  });
};

export const updateShow = showWithChanges => dispatch => {
  return create.patch(`/api-v2/channels/${showWithChanges.id}`, showWithChanges).then(response => {
    dispatch({ type: UPDATE_SHOW, payload: response.body });
    return Promise.resolve();
  });
};

export const deleteShow = showId => dispatch => {
  return create.delete(`/api-v2/channels/${showId}`).then(() => {
    dispatch({ type: DELETE_SHOW, payload: showId });
    return Promise.resolve();
  });
};
